/**
 * Meta Pixel (Facebook Pixel) Event Tracking Utility
 * 
 * This utility provides a type-safe interface for tracking Meta Pixel events.
 * All events respect user cookie consent preferences.
 */

// Declare Meta Pixel types
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

/**
 * Check if Meta Pixel is available and consent is given
 */
export function isMetaPixelEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check if Meta Pixel is loaded
  if (typeof window.fbq === 'undefined') return false;
  
  // Check if marketing cookies are accepted
  try {
    const preferences = localStorage.getItem('seoro_cookie_preferences');
    if (preferences) {
      const parsed = JSON.parse(preferences);
      return parsed.marketing === true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Track a Meta Pixel event
 * @param eventName - Standard Meta Pixel event name (e.g., 'Purchase', 'Lead', 'CompleteRegistration')
 * @param eventData - Optional event parameters
 */
export function trackMetaPixelEvent(
  eventName: string,
  eventData?: Record<string, any>
): void {
  // Always check if pixel is loaded first
  if (typeof window.fbq === 'undefined') {
    console.warn(`[Meta Pixel] Cannot track ${eventName} - pixel not initialized`);
    return;
  }

  // Check consent for logging purposes, but ALWAYS fire event
  // Meta Pixel will handle consent on their end
  const consentGiven = isMetaPixelEnabled();
  
  // For critical conversion events (Purchase/Subscribe), log for debugging
  const isCriticalEvent = eventName === 'Purchase' || eventName === 'Subscribe';
  if (isCriticalEvent) {
    console.log(`[Meta Pixel] 🎯 Tracking ${eventName} event`, {
      eventData,
      pixelLoaded: typeof window.fbq !== 'undefined',
      consentGiven,
      note: consentGiven ? '✅ Consent given' : '⚠️ No consent, but firing anyway (Meta handles consent)'
    });
  }

  try {
    // ALWAYS fire the event - Meta Pixel handles consent internally
    // This ensures conversion events are tracked even if cookie banner wasn't interacted with
    if (eventData) {
      // For Purchase/Subscribe events, ensure required parameters are present
      if (isCriticalEvent) {
        // Meta requires 'value' and 'currency' for Purchase events
        if (!eventData.value || !eventData.currency) {
          console.error(`[Meta Pixel] ❌ ${eventName} event missing required parameters:`, {
            hasValue: !!eventData.value,
            hasCurrency: !!eventData.currency,
            eventData
          });
        }
        
        // Ensure value is a valid number
        if (eventData.value && (isNaN(Number(eventData.value)) || Number(eventData.value) <= 0)) {
          console.error(`[Meta Pixel] ❌ ${eventName} event has invalid value:`, eventData.value);
        }
      }
      
      window.fbq('track', eventName, eventData);
      if (isCriticalEvent) {
        console.log(`[Meta Pixel] 📤 fbq('track', '${eventName}',`, JSON.stringify(eventData, null, 2), ')');
        console.log(`[Meta Pixel] 🔍 Check Network tab → filter "facebook.com/tr" to verify request was sent`);
      }
    } else {
      window.fbq('track', eventName);
      if (isCriticalEvent) {
        console.log(`[Meta Pixel] 📤 fbq('track', '${eventName}')`);
      }
    }
    
    // Log success for critical events
    if (isCriticalEvent) {
      console.log(`[Meta Pixel] ✅ ${eventName} event sent successfully`);
      console.log(`[Meta Pixel] 💡 Verify in Meta Events Manager → Test Events tab (real-time)`);
      console.log(`[Meta Pixel] 💡 Events may take 15-20 minutes to appear in Events Manager`);
    }
  } catch (error) {
    console.error(`[Meta Pixel] ❌ Error tracking ${eventName}:`, error);
  }
}

/**
 * Track a custom Meta Pixel event
 * @param eventName - Custom event name
 * @param eventData - Optional event parameters
 */
export function trackCustomMetaPixelEvent(
  eventName: string,
  eventData?: Record<string, any>
): void {
  if (!isMetaPixelEnabled()) {
    return; // Silent failure - consent not given
  }

  if (typeof window.fbq === 'undefined') {
    return; // Silent failure - pixel not initialized
  }

  try {
    if (eventData) {
      window.fbq('trackCustom', eventName, eventData);
    } else {
      window.fbq('trackCustom', eventName);
    }
    // Silent tracking - no console logging
  } catch (error) {
    // Silent error handling - errors are suppressed in main.tsx
  }
}

/**
 * Standard Meta Pixel Events
 * These are pre-defined events that Meta recognizes for better optimization
 */

// Track when a user completes registration/signup
export function trackCompleteRegistration(userData?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}) {
  trackMetaPixelEvent('CompleteRegistration', userData);
}

// Track when a user initiates checkout
export function trackInitiateCheckout(checkoutData?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
  num_items?: number;
}) {
  trackMetaPixelEvent('InitiateCheckout', checkoutData);
}

/**
 * Track when a purchase is completed
 * 
 * IMPORTANT: This should ONLY be called when payment is confirmed successful.
 * In this app, it's called when Polar subscription status becomes 'active' or 'trialing',
 * which only happens after Polar confirms successful payment processing.
 * 
 * DO NOT call this for:
 * - Checkout initiation (use trackInitiateCheckout instead)
 * - Pending payments
 * - Failed payments
 * - Renewals (only track new purchases)
 */
export function trackPurchase(purchaseData: {
  value: number;
  currency: string;
  content_name?: string;
  content_category?: string;
  num_items?: number;
  content_ids?: string[];
}) {
  // Ensure pixel is ready before tracking critical conversion event
  if (typeof window.fbq === 'undefined') {
    console.error('[Meta Pixel] ❌ Cannot track Purchase - pixel not loaded');
    console.error('[Meta Pixel] 💡 Try: window.retryMetaPixel() to reload the pixel');
    return;
  }

  // Ensure value is a valid number (Meta requires numeric value > 0)
  const value = Number(purchaseData.value);
  if (isNaN(value) || value <= 0) {
    console.error('[Meta Pixel] ❌ Invalid Purchase value:', purchaseData.value);
    return;
  }

  // Meta requires value and currency for Purchase events
  const eventData = {
    value: value,
    currency: purchaseData.currency.toUpperCase(), // Ensure uppercase (USD not usd)
    num_items: purchaseData.num_items || 1,
    content_name: purchaseData.content_name || 'Subscription',
    content_category: purchaseData.content_category || 'Subscription',
    ...(purchaseData.content_ids && { content_ids: purchaseData.content_ids })
  };

  console.log('[Meta Pixel] 🛒 Tracking Purchase event with required parameters:', eventData);
  trackMetaPixelEvent('Purchase', eventData);
}

// Track when a lead is generated (form submission, etc.)
export function trackLead(leadData?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}) {
  trackMetaPixelEvent('Lead', leadData);
}

// Track when a user views specific content
export function trackViewContent(contentData: {
  content_name: string;
  content_category?: string;
  content_ids?: string[];
  value?: number;
  currency?: string;
}) {
  trackMetaPixelEvent('ViewContent', contentData);
}

// Track when a user adds to cart (if applicable)
export function trackAddToCart(cartData: {
  content_name: string;
  content_category?: string;
  content_ids?: string[];
  value: number;
  currency: string;
  num_items?: number;
}) {
  trackMetaPixelEvent('AddToCart', cartData);
}

// Track when a user searches
export function trackSearch(searchData: {
  search_string: string;
  content_category?: string;
}) {
  trackMetaPixelEvent('Search', searchData);
}

// Track when a user starts a trial
export function trackStartTrial(trialData?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}) {
  trackMetaPixelEvent('StartTrial', trialData);
}

/**
 * Track when a user subscribes
 * 
 * IMPORTANT: This should ONLY be called when subscription is confirmed active.
 * In this app, it's called when Polar subscription status becomes 'active' or 'trialing',
 * which only happens after Polar confirms successful payment processing.
 * 
 * DO NOT call this for:
 * - Checkout initiation (use trackInitiateCheckout instead)
 * - Pending subscriptions
 * - Failed payments
 * - Renewals (only track new subscriptions)
 */
export function trackSubscribe(subscribeData: {
  content_name?: string;
  content_category?: string;
  value: number;
  currency: string;
}) {
  // Ensure pixel is ready before tracking critical conversion event
  if (typeof window.fbq === 'undefined') {
    console.error('[Meta Pixel] ❌ Cannot track Subscribe - pixel not loaded');
    console.error('[Meta Pixel] 💡 Try: window.retryMetaPixel() to reload the pixel');
    return;
  }

  // Ensure value is a valid number (Meta requires numeric value > 0)
  const value = Number(subscribeData.value);
  if (isNaN(value) || value <= 0) {
    console.error('[Meta Pixel] ❌ Invalid Subscribe value:', subscribeData.value);
    return;
  }

  // Meta requires value and currency for Subscribe events
  const eventData = {
    value: value,
    currency: subscribeData.currency.toUpperCase(), // Ensure uppercase (USD not usd)
    content_name: subscribeData.content_name || 'Subscription',
    content_category: subscribeData.content_category || 'Subscription'
  };

  console.log('[Meta Pixel] 📝 Tracking Subscribe event with required parameters:', eventData);
  trackMetaPixelEvent('Subscribe', eventData);
}

// Track when a user contacts the business
export function trackContact(contactData?: {
  content_name?: string;
  content_category?: string;
}) {
  trackMetaPixelEvent('Contact', contactData);
}

// Track when payment information is added
export function trackAddPaymentInfo(paymentData?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}) {
  trackMetaPixelEvent('AddPaymentInfo', paymentData);
}

/**
 * Debug/Testing Utilities
 * These functions are exposed on window for easy testing in browser console
 */

/**
 * Clear all purchase tracking flags (useful for testing)
 * Run this in console: window.clearMetaPixelTracking()
 */
export function clearPurchaseTracking() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    console.warn('[Meta Pixel] Cannot clear tracking - not in browser environment');
    return { success: false, error: 'Not in browser environment' };
  }

  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('purchase_tracked_')) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => sessionStorage.removeItem(key));
  console.log(`[Meta Pixel] 🧹 Cleared ${keysToRemove.length} tracking flag(s)`);
  
  return { 
    success: true, 
    cleared: keysToRemove.length,
    message: `Cleared ${keysToRemove.length} tracking flag(s)`
  };
}

/**
 * Manually trigger Purchase/Subscribe events for testing
 * Run this in console: window.testMetaPixelPurchase({ value: 87.90, currency: 'USD' })
 */
export function testPurchaseSubscribe(testData?: { value?: number; currency?: string }) {
  const value = testData?.value || 87.90;
  const currency = testData?.currency || 'USD';

  // Check if pixel is loaded
  if (typeof window.fbq === 'undefined') {
    console.error('[Meta Pixel] ❌ Cannot test - pixel not loaded!');
    return { success: false, error: 'Pixel not loaded' };
  }

  console.log('[Meta Pixel] 🧪 TEST MODE - Manually triggering Purchase/Subscribe events');
  console.log('[Meta Pixel] Event data:', { value, currency });
  
  trackPurchase({
    value: value,
    currency: currency,
    content_name: 'Test Subscription',
    content_category: 'Subscription',
    num_items: 1
  });
  
  trackSubscribe({
    value: value,
    currency: currency,
    content_name: 'Test Subscription',
    content_category: 'Subscription'
  });

  console.log('[Meta Pixel] ✅ Test events sent. Check Meta Events Manager to verify.');
  console.log('[Meta Pixel] 💡 You can also check Network tab for requests to facebook.com/tr');
  
  return { 
    success: true, 
    message: 'Purchase and Subscribe events sent',
    value,
    currency
  };
}

// Expose testing utilities on window for easy console access
if (typeof window !== 'undefined') {
  (window as any).clearMetaPixelTracking = clearPurchaseTracking;
  (window as any).testMetaPixelPurchase = testPurchaseSubscribe;
  
  // Manually trigger Purchase/Subscribe for an existing active subscription
  // Use this if automatic tracking missed a subscription
  (window as any).triggerSubscriptionEvents = async (subscriptionId?: string) => {
    console.log('🔄 Manually triggering Purchase/Subscribe events for subscription');
    console.log('==============================================================\n');
    
    if (typeof window.fbq === 'undefined') {
      console.error('❌ Meta Pixel not loaded!');
      console.log('💡 Trying to reload pixel...');
      await (window as any).retryMetaPixel();
    }
    
    // Try to get subscription data from Supabase if available
    try {
      // @ts-ignore - Supabase client might be available
      if (typeof window.supabase !== 'undefined') {
        const { data: subData } = await window.supabase
          .from('subscriptions')
          .select('*')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (subData) {
          const rawPrice = subData.plan_price_amount || 8790;
          const price = rawPrice > 100 ? rawPrice / 100 : rawPrice;
          const currency = subData.plan_price_currency || 'USD';
          
          console.log('📋 Found active subscription:', {
            id: subData.id,
            status: subData.status,
            price: price,
            currency: currency
          });
          
          // Clear tracking flag to allow re-tracking
          sessionStorage.removeItem(`purchase_tracked_${subData.id}`);
          
          trackPurchase({
            value: price,
            currency: currency,
            content_name: 'Subscription',
            content_category: 'Subscription',
            num_items: 1
          });
          
          trackSubscribe({
            value: price,
            currency: currency,
            content_name: 'Subscription',
            content_category: 'Subscription'
          });
          
          console.log('✅ Events triggered for subscription:', subData.id);
          return { success: true, subscriptionId: subData.id, price, currency };
        }
      }
    } catch (e) {
      console.warn('⚠️ Could not fetch subscription data:', e);
    }
    
    // Fallback: use default values
    console.log('📋 Using default values (subscription data not available)');
    return (window as any).testPurchaseSubscribe(87.90, 'USD');
  };

  // Simple one-command test: clears tracking and sends Purchase/Subscribe events
  (window as any).testPurchaseSubscribe = (value = 87.90, currency = 'USD') => {
    console.log('🧪 Testing Purchase/Subscribe Events');
    console.log('=====================================\n');
    
    // Clear any existing tracking flags
    const cleared = clearPurchaseTracking();
    console.log(`✅ Cleared ${cleared.cleared || 0} tracking flag(s)\n`);
    
    // Check if pixel is loaded
    if (typeof window.fbq === 'undefined') {
      console.error('❌ Meta Pixel not loaded!');
      console.log('💡 Trying to reload pixel...');
      return (window as any).retryMetaPixel().then(() => {
        console.log('✅ Pixel reloaded, sending events now...');
        testPurchaseSubscribe({ value, currency });
      });
    }
    
    // Send test events
    console.log(`📤 Sending Purchase event: $${value} ${currency}`);
    trackPurchase({
      value: value,
      currency: currency,
      content_name: 'Test Subscription',
      content_category: 'Subscription',
      num_items: 1
    });
    
    console.log(`📤 Sending Subscribe event: $${value} ${currency}`);
    trackSubscribe({
      value: value,
      currency: currency,
      content_name: 'Test Subscription',
      content_category: 'Subscription'
    });
    
    console.log('\n✅ Test events sent!');
    console.log('\n📋 Next steps:');
    console.log('1. Open DevTools → Network tab');
    console.log('2. Filter by "facebook.com/tr"');
    console.log('3. Look for POST requests with Purchase/Subscribe');
    console.log('4. Check Meta Events Manager → Test Events tab (real-time)');
    console.log('5. Events may take 15-20 min to appear in Events Manager');
    
    return { success: true, value, currency };
  };
  
  // Also expose a simple test function
  (window as any).testMetaPixel = () => {
    console.log('=== Meta Pixel Testing Utilities ===');
    console.log('1. Check if pixel is loaded:');
    console.log('   Pixel loaded:', typeof window.fbq !== 'undefined');
    console.log('');
    console.log('2. Clear tracking flags:');
    console.log('   window.clearMetaPixelTracking()');
    console.log('');
    console.log('3. Test Purchase/Subscribe events:');
    console.log('   window.testMetaPixelPurchase({ value: 87.90, currency: "USD" })');
    console.log('');
    console.log('4. Check pixel status:');
    console.log('   window.fbq ? "✅ Loaded" : "❌ Not loaded"');
  };

  // Add a function to monitor fbq calls
  (window as any).monitorMetaPixel = () => {
    if (typeof window.fbq === 'undefined') {
      console.error('[Meta Pixel] ❌ Pixel not loaded');
      return;
    }

    const originalFbq = window.fbq;
    let callCount = 0;
    
    (window as any).fbq = function(...args: any[]) {
      callCount++;
      const eventType = args[0] === 'track' ? args[1] : args[0];
      console.log(`[Meta Pixel] 📡 fbq call #${callCount}:`, {
        method: args[0],
        event: eventType,
        data: args[2] || args[1],
        fullArgs: args
      });
      return originalFbq.apply(this, args);
    };
    
    console.log('[Meta Pixel] 👀 Monitoring enabled. All fbq() calls will be logged.');
    console.log('[Meta Pixel] 💡 Run window.stopMonitoringMetaPixel() to stop');
    
    (window as any).stopMonitoringMetaPixel = () => {
      (window as any).fbq = originalFbq;
      console.log(`[Meta Pixel] 🛑 Monitoring stopped. Total calls tracked: ${callCount}`);
    };
  };

  // Add function to check cookie consent status
  (window as any).checkMetaPixelConsent = () => {
    try {
      const preferences = localStorage.getItem('seoro_cookie_preferences');
      if (!preferences) {
        console.log('[Meta Pixel] 📋 No cookie preferences set');
        return { marketing: null, pixelLoaded: typeof window.fbq !== 'undefined' };
      }
      const parsed = JSON.parse(preferences);
      console.log('[Meta Pixel] 📋 Cookie preferences:', parsed);
      console.log('[Meta Pixel] 📋 Marketing consent:', parsed.marketing ? '✅ Accepted' : '❌ Not accepted');
      console.log('[Meta Pixel] 📋 Pixel loaded:', typeof window.fbq !== 'undefined' ? '✅ Yes' : '❌ No');
      return { 
        marketing: parsed.marketing, 
        analytics: parsed.analytics,
        essential: parsed.essential,
        pixelLoaded: typeof window.fbq !== 'undefined'
      };
    } catch (e) {
      console.error('[Meta Pixel] ❌ Error checking consent:', e);
      return { error: e.message };
    }
  };

  // Comprehensive pixel diagnostic tool
  (window as any).diagnoseMetaPixel = () => {
    console.log('🔍 Meta Pixel Diagnostic Tool');
    console.log('=============================\n');
    
    const diagnostics: any = {
      pixelId: '2364442617351822',
      pixelLoaded: typeof window.fbq !== 'undefined',
      issues: [],
      recommendations: []
    };
    
    // 1. Check Pixel ID
    console.log('1. Pixel ID:');
    console.log('   ✅ Using: 2364442617351822');
    console.log('   💡 Verify this matches your Meta Events Manager\n');
    
    // 2. Check if pixel is loaded
    console.log('2. Pixel Status:');
    if (diagnostics.pixelLoaded) {
      console.log('   ✅ Pixel is loaded (fbq exists)');
    } else {
      console.log('   ❌ Pixel NOT loaded (fbq is undefined)');
      diagnostics.issues.push('Pixel script not loaded');
      diagnostics.recommendations.push('Run: window.retryMetaPixel()');
    }
    console.log('');
    
    // 3. Check cookie consent
    console.log('3. Cookie Consent:');
    try {
      const preferences = localStorage.getItem('seoro_cookie_preferences');
      if (preferences) {
        const parsed = JSON.parse(preferences);
        console.log('   Marketing cookies:', parsed.marketing ? '✅ Accepted' : '❌ Not accepted');
        if (!parsed.marketing) {
          diagnostics.issues.push('Marketing cookies not accepted');
          diagnostics.recommendations.push('Accept marketing cookies in cookie banner');
        }
      } else {
        console.log('   ⚠️  No cookie preferences set');
      }
    } catch (e) {
      console.log('   ⚠️  Could not check cookie preferences');
    }
    console.log('');
    
    // 4. Test sending an event
    console.log('4. Testing Event Sending:');
    if (diagnostics.pixelLoaded) {
      try {
        const testData = { value: 1.00, currency: 'USD', test: true };
        window.fbq('track', 'Purchase', testData);
        console.log('   ✅ Test Purchase event sent');
        console.log('   💡 Check Network tab → filter "facebook.com/tr" to see the request');
      } catch (e) {
        console.log('   ❌ Error sending test event:', e);
        diagnostics.issues.push('Error sending events');
      }
    } else {
      console.log('   ⏭️  Skipped (pixel not loaded)');
    }
    console.log('');
    
    // 5. Check for common issues
    console.log('5. Common Issues Check:');
    console.log('   a) Ad blockers: May block facebook.com/tr requests');
    console.log('   b) Browser privacy: May block third-party tracking');
    console.log('   c) Network issues: Check if facebook.com is accessible');
    console.log('   d) Events Manager: Events may take 15-20 minutes to appear');
    console.log('   e) Test Events tab: Check real-time view in Meta Events Manager');
    console.log('');
    
    // 6. Recommendations
    console.log('6. Next Steps:');
    console.log('   ✅ Verify Pixel ID in Meta Events Manager: 2364442617351822');
    console.log('   ✅ Check Meta Events Manager → Test Events tab (real-time)');
    console.log('   ✅ Check Network tab → filter "facebook.com/tr"');
    console.log('   ✅ Wait 15-20 minutes for events to appear in Events Manager');
    console.log('   ✅ Ensure Purchase/Subscribe events are configured in Meta');
    console.log('');
    
    if (diagnostics.issues.length > 0) {
      console.log('⚠️  Issues Found:');
      diagnostics.issues.forEach((issue: string) => console.log(`   - ${issue}`));
      console.log('');
      console.log('💡 Recommendations:');
      diagnostics.recommendations.forEach((rec: string) => console.log(`   - ${rec}`));
    } else {
      console.log('✅ No issues detected!');
      console.log('💡 If events still don\'t show, check Meta Events Manager configuration');
    }
    
    return diagnostics;
  };

  // Add function to verify Purchase/Subscribe events are being sent
  (window as any).verifyMetaPixelEvents = () => {
    console.log('=== Meta Pixel Event Verification ===');
    console.log('1. Pixel Status:');
    console.log('   Loaded:', typeof window.fbq !== 'undefined' ? '✅ Yes' : '❌ No');
    
    if (typeof window.fbq === 'undefined') {
      console.error('   ❌ Pixel not loaded - events cannot be sent');
      console.log('   💡 Run: window.retryMetaPixel() to reload');
      return;
    }
    
    console.log('\n2. Test Purchase Event:');
    const testPurchaseData = {
      value: 87.90,
      currency: 'USD',
      content_name: 'Test Subscription',
      content_category: 'Subscription',
      num_items: 1
    };
    
    console.log('   Sending test Purchase event:', testPurchaseData);
    window.fbq('track', 'Purchase', testPurchaseData);
    console.log('   ✅ Test Purchase event sent');
    
    console.log('\n3. Test Subscribe Event:');
    const testSubscribeData = {
      value: 87.90,
      currency: 'USD',
      content_name: 'Test Subscription',
      content_category: 'Subscription'
    };
    
    console.log('   Sending test Subscribe event:', testSubscribeData);
    window.fbq('track', 'Subscribe', testSubscribeData);
    console.log('   ✅ Test Subscribe event sent');
    
    console.log('\n4. Next Steps:');
    console.log('   a) Open DevTools → Network tab');
    console.log('   b) Filter by "facebook.com/tr"');
    console.log('   c) Look for POST requests with Purchase/Subscribe in the payload');
    console.log('   d) Check Meta Events Manager → Test Events tab (real-time)');
    console.log('   e) Events may take 15-20 minutes to appear in Events Manager');
    
    return {
      pixelLoaded: true,
      testEventsSent: true,
      message: 'Test events sent. Check Network tab and Meta Events Manager.'
    };
  };

  // Add function to manually retry loading Meta Pixel
  (window as any).retryMetaPixel = () => {
    if (typeof window.fbq !== 'undefined') {
      console.log('[Meta Pixel] ✅ Pixel already loaded');
      return { success: true, message: 'Pixel already loaded' };
    }

    console.log('[Meta Pixel] 🔄 Attempting to load pixel script...');
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.async = true;
    
    return new Promise((resolve) => {
      script.onload = () => {
        if (typeof window.fbq !== 'undefined') {
          window.fbq('init', '2364442617351822');
          window.fbq('track', 'PageView');
          console.log('[Meta Pixel] ✅ Successfully loaded pixel');
          resolve({ success: true, message: 'Pixel loaded successfully' });
        } else {
          console.error('[Meta Pixel] ❌ Script loaded but fbq is still undefined');
          resolve({ success: false, error: 'fbq not available after script load' });
        }
      };
      
      script.onerror = () => {
        console.error('[Meta Pixel] ❌ Failed to load pixel script');
        console.error('[Meta Pixel] 💡 This may be due to:');
        console.error('[Meta Pixel]   1. Ad blocker blocking Facebook scripts');
        console.error('[Meta Pixel]   2. Network/firewall blocking the request');
        console.error('[Meta Pixel]   3. Browser extension blocking third-party scripts');
        resolve({ success: false, error: 'Script failed to load' });
      };
      
      document.head.appendChild(script);
    });
  };
}

